import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, RotateCcw, ArrowRight } from 'lucide-react';
import { highlightText } from '../../../utils/highlightText';
import { contactHref } from '../../../utils/contact';
import AccessCodeForm from './AccessCodeForm';
import PayButton from './PayButton';

const ANSWER_PREVIEW_CHARS = 900;

// Models often answer in Markdown; the transcript view shows plain text.
const toPlainText = (text = '') =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .trim();

const truncate = (text) => {
  if (text.length <= ANSWER_PREVIEW_CHARS) return text;
  const cut = text.slice(0, ANSWER_PREVIEW_CHARS);
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
};

const REPORT_ITEMS = [
  '12 preguntas como las que hacen tus clientes, no solo una',
  'En qué porcentaje de respuestas apareces tú y cada competidor',
  'Por qué la IA los elige a ellos: reseñas, ficha de Google, menciones y web',
  'Tu plan GEO paso a paso, ordenado por impacto',
  'Los textos ya escritos: título de tu web, ficha de Google, preguntas frecuentes y mensaje para pedir reseñas'
];

const CheckResult = ({ result, input, onReset }) => {
  const recommendations = result.recommendations || [];
  const mentioned = Boolean(result.mentioned);
  const answer = truncate(toPlainText(result.answer));
  const topCompetitor = recommendations.find((_, i) => i + 1 !== result.position)?.name;

  let summary;
  if (mentioned) {
    summary = result.position
      ? `Apareces en el puesto ${result.position} de los ${recommendations.length} negocios que recomienda. Buena señal, pero es una sola pregunta: tus clientes hacen muchas.`
      : 'Te menciona en su respuesta. Buena señal, pero es una sola pregunta: tus clientes hacen muchas.';
  } else if (recommendations.length > 0) {
    summary = `Cuando alguien pregunta lo mismo que un cliente tuyo, la IA recomienda ${recommendations.length} ${
      recommendations.length === 1 ? 'negocio' : 'negocios'
    }. Ninguno es el tuyo.`;
  } else {
    summary = 'La IA respondió sin recomendar ningún negocio concreto, y tampoco el tuyo.';
  }

  const mailto = contactHref(
    `Informe completo · ${input.businessName} (${input.city})`,
    `Hola, quiero el informe completo de visibilidad en IA para ${input.businessName} (${input.sector}, ${input.city}).` +
      (result.checkId ? `\n\nReferencia: ${result.checkId}` : '')
  );

  return (
    <div>
      <p className="lp-kicker">
        Resultado · {input.businessName} · {input.city}
      </p>

      <h2 className="lp-display mt-5 text-[clamp(3rem,8vw,6.75rem)]">
        La IA{' '}
        <span className={`lp-serif ${mentioned ? 'text-[var(--lp-lime)]' : 'text-[var(--lp-alarm)]'}`}>
          {mentioned ? 'sí' : 'no'}
        </span>{' '}
        te nombra.
      </h2>
      <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[var(--lp-muted)] sm:text-xl">{summary}</p>

      <div className="mt-14 grid gap-8 lg:grid-cols-5">
        <div className="rounded-[26px] bg-[var(--lp-paper)] p-6 text-[var(--lp-ink)] sm:p-8 lg:col-span-3">
          <p className="lp-mono text-[11px] uppercase tracking-[0.14em] text-black/45">
            La pregunta que hicimos
          </p>
          <div className="mt-4 flex justify-end">
            <p className="max-w-[90%] rounded-[20px] rounded-br-md bg-[var(--lp-ink)] px-4 py-3 text-base leading-snug text-[var(--lp-paper)]">
              {result.question}
            </p>
          </div>
          <p className="mt-6 lp-mono text-[11px] uppercase tracking-[0.14em] text-black/45">Lo que respondió</p>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-black/75">
            {highlightText(
              answer,
              mentioned ? input.businessName : null,
              'rounded bg-[var(--lp-lime)] px-1 text-[var(--lp-ink)]'
            )}
          </p>
        </div>

        <aside className="lg:col-span-2">
          <p className="lp-kicker">{mentioned ? 'Con quién compartes la respuesta' : 'Quién aparece en tu lugar'}</p>
          {recommendations.length > 0 ? (
            <ol className="mt-5 divide-y divide-[var(--lp-line)] border-y border-[var(--lp-line)]">
              {recommendations.map((item, i) => {
                const isYou = mentioned && result.position === i + 1;
                return (
                  <li key={`${item.name}-${i}`} className="flex items-baseline gap-4 py-4">
                    <span className="lp-mono text-xs text-[var(--lp-muted)]">{String(i + 1).padStart(2, '0')}</span>
                    <span className={`text-lg font-semibold tracking-[-0.01em] ${isYou ? 'text-[var(--lp-lime)]' : ''}`}>
                      {item.name}
                      {isYou && <span className="ml-2 lp-mono text-xs font-normal uppercase">tú</span>}
                    </span>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="mt-5 text-[var(--lp-muted)]">No nombró ningún negocio concreto.</p>
          )}
        </aside>
      </div>

      <div className="mt-16 overflow-hidden rounded-[28px] border border-[var(--lp-line)] bg-[var(--lp-surface)] p-6 sm:p-10">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <p className="lp-kicker">Informe completo</p>
            <h3 className="lp-display mt-5 text-[clamp(2.1rem,4.2vw,3.4rem)]">
              {mentioned ? 'Dónde más apareces.' : 'Por qué te ganan.'}
              <br />
              <span className="lp-serif text-[var(--lp-lime)]">
                {mentioned ? 'Y cómo no dejar de hacerlo.' : 'Y cómo darles la vuelta.'}
              </span>
            </h3>
            <ul className="mt-8 space-y-3">
              {REPORT_ITEMS.map((item) => (
                <li key={item} className="flex gap-3 text-[var(--lp-muted)]">
                  <ArrowRight size={18} className="mt-1 shrink-0 text-[var(--lp-lime)]" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <PayButton checkId={result.checkId} website={input.website} contactHref={mailto}>
                <Link
                  to="/informe/ejemplo"
                  className="font-semibold underline decoration-[var(--lp-lime)] decoration-2 underline-offset-8"
                >
                  Ver un informe de ejemplo
                </Link>
              </PayButton>
            </div>
            <div className="mt-8">
              <AccessCodeForm checkId={result.checkId} website={input.website} />
            </div>
          </div>

          <div aria-hidden="true" className="space-y-4">
            {[
              'Otras preguntas de tus clientes',
              topCompetitor ? `Por qué ${topCompetitor} te supera` : 'Qué hace la IA elegir a otros',
              'Tu plan de acción'
            ].map((title) => (
              <div key={title} className="rounded-2xl border border-[var(--lp-line)] p-5">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold">{title}</span>
                  <Lock size={16} className="shrink-0 text-[var(--lp-muted)]" />
                </div>
                <div className="mt-4 space-y-2.5">
                  <div className="lp-skeleton h-2.5 w-11/12" />
                  <div className="lp-skeleton h-2.5 w-4/5" />
                  <div className="lp-skeleton h-2.5 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-2xl text-sm leading-relaxed text-[var(--lp-muted)]">
          Consulta hecha a un modelo de IA con búsqueda web en tiempo real, formulada como la haría un cliente.
          Cada asistente (ChatGPT, Gemini, Perplexity…) responde a su manera y las respuestas cambian con el
          tiempo: tómalo como una señal, no como una foto exacta de lo que verá cada persona.
        </p>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[var(--lp-line)] px-5 py-3 text-sm font-semibold transition-colors hover:border-[var(--lp-text)]"
        >
          <RotateCcw size={16} aria-hidden="true" />
          Probar con otro negocio
        </button>
      </div>
    </div>
  );
};

export default CheckResult;
